import { useEffect, useState } from "react";
import LegalDocumentLayout from "./LegalDocumentLayout";
import { sanitizeLegalHtml } from "@/lib/legalFormats";

/**
 * BLOCK 54 Item 1 ,  public legal-page renderer that prefers the DB's
 * ``is_current=True`` content_html over the hardcoded JSX fallback.
 *
 * The compiled JSX children are still shipped in the bundle and used
 * verbatim when:
 *   * the fetch fails / times out
 *   * the endpoint returns ``has_db_version=false``
 *   * ``content_html`` is empty or shorter than a sanity floor
 *
 * The optional ``lastUpdatedFallback`` prop is what the layout shows when
 * we're rendering the compiled JSX. When we render from DB we prefer
 * ``effective_date`` from the row so admin edits carry through.
 */
export default function LegalPageWithOverride({
  slug,
  title,
  lastUpdatedFallback,
  children,
}) {
  const [state, setState] = useState({ loading: true, dbHtml: null, effective: null, version: null });

  useEffect(() => {
    let alive = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/legal-pages/${slug}`;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return;
        if (j && j.has_db_version && j.content_html && j.content_html.length > 100) {
          setState({
            loading: false,
            dbHtml: j.content_html,
            effective: j.effective_date || null,
            version: j.version || null,
          });
        } else {
          setState({ loading: false, dbHtml: null, effective: null, version: null });
        }
      })
      .catch(() => {
        if (alive) setState({ loading: false, dbHtml: null, effective: null, version: null });
      });
    return () => { alive = false; };
  }, [slug]);

  if (state.dbHtml) {
    // Format ISO date "2026-07-19" as "July 19, 2026" to match the JSX.
    let lastUpdated = state.effective || lastUpdatedFallback;
    if (state.effective && /^\d{4}-\d{2}-\d{2}/.test(state.effective)) {
      const d = new Date(state.effective + "T00:00:00Z");
      lastUpdated = d.toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
      });
    }
    return (
      <LegalDocumentLayout title={title} lastUpdated={lastUpdated}>
        <div
          data-testid={`legal-db-content-${slug}`}
          data-legal-version={state.version}
          dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(state.dbHtml) }}
        />
      </LegalDocumentLayout>
    );
  }

  // Fallback: JSX children unchanged ,  bit-identical to the old render.
  return children;
}
